import React from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import ReportIssuePage from './pages/ReportIssuePage';
import BiPage from './pages/BiPage';
import MlPage from './pages/MlPage';
import StrategyPage from './pages/StrategyPage';
import DataEngineeringPage from './pages/DataEngineeringPage';
import AuthPage from './pages/AuthPage';
import DashboardPage from './pages/DashboardPage';
import ActivityLogsPage from './pages/ActivityLogsPage';
import ProfilePage from './pages/ProfilePage';
import NativeVisualsPage from './pages/NativeVisualsPage';
import ProductPage from './pages/ProductPage';
import TemplatesPage from './pages/TemplatesPage';
import NotFoundPage from './pages/NotFoundPage';
import PricingPage from './pages/PricingPage';
import ProtectedRoute from './components/ProtectedRoute';
import LineChartPage from './pages/LineChartPage';
import TwoDColumnsPage from './pages/2DColumnsPage/2DColumnsPage';
import DonutChartPage from './pages/DonutChartPage/DonutChartPage';
import { AuthProvider } from './contexts/AuthContext';
import AutomobilesPage from './pages/AutomobilesPage';
import EducationPage from './pages/EducationPage';
import RealEstatePage from './pages/RealEstatePage';
import HealthcarePage from './pages/HealthcarePage';
import RetailPage from './pages/RetailPage';
import HotelsPage from './pages/HotelsPage';
import ScrollToTop from './utils/ScrollToTop';
import { ToastProvider, ToastContainer } from './components/Toast';
import AdminRoute from './components/AdminRoute';
import AccessDeniedPage from './pages/AccessDeniedPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminUserDetails from './pages/AdminUserDetails';
import AdminSectors from './pages/AdminSectors';
import AdminSchools from './pages/AdminSchools';
import AdminSchoolDetails from './pages/AdminSchoolDetails';
import AdminCreateTablePage from './pages/AdminCreateTablePage';
import AdminTablesPage from './pages/AdminTablesPage';
import AdminAddDataPage from './pages/AdminAddDataPage';
import AdminViewDataPage from './pages/AdminViewDataPage';
import AdminEditTablePage from './pages/AdminEditTablePage';
import AdminFields from './pages/AdminFields';
import VisualsPage from './pages/VisualsPage';
import CreateTablePage from './pages/CreateTablePage';
import TablesPage from './pages/TablesPage';
import AddDataPage from './pages/AddDataPage';
import ViewDataPage from './pages/ViewDataPage';
import UploadPage from './pages/UploadPage';
import EditTablePage from './pages/EditTablePage';
import ReportViewPage from './pages/ReportViewPage';
import SectorGuard from './components/SectorGuard';
import RequirePaidProduct from './components/RequirePaidProduct';
import RequireActivePlan from './components/RequireActivePlan';

function App() {
  const location = useLocation();

  // Hide header on auth page and all protected pages (they have sidebar)
  // Prefix matched, so '/admin' covers /admin-dashboard, /admin/entities/*
  // and /admin/fields, and the parameterised children need no entry of
  // their own. /admin/fields was previously missing and showed the public
  // header and footer on a signed-in admin page.
  const protectedPaths = [
    '/login',
    '/pricing',
    '/dashboard',
    '/activity',
    '/profile',
    '/erp',
    '/templates',
    '/visuals',
    '/sectors',
    '/analytics',
    '/admin'
  ];
  const isReportPopout = location.pathname.startsWith('/report-view');
  // The marketing header and footer belong to the public site. Signed-in
  // pages have their own sidebar chrome, so both are hidden there.
  const showPublicChrome =
    !isReportPopout && !protectedPaths.some(path => location.pathname.startsWith(path));
  const showHeader = showPublicChrome;

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <ToastProvider>
        <AuthProvider>
          <div className="appLayout">
            <ScrollToTop />
            {showHeader && <Header />}
            <main className="appMain">
              <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/bi" element={<BiPage />} />
            <Route path="/ml" element={<MlPage />} />
            <Route path="/strategy" element={<StrategyPage />} />
            <Route path="/data-engineering" element={<DataEngineeringPage />} />
            <Route path="/products/:slug" element={<ProductPage />} />
            <Route path="/linechart" element={<LineChartPage />} />
            <Route path="/2dcolumns" element={<TwoDColumnsPage />} />
            <Route path="/donutchart" element={<DonutChartPage />} />
            <Route path="/automobile" element={<AutomobilesPage />} />
            <Route path="/education" element={<EducationPage />} />
            <Route path="/real-estate" element={<RealEstatePage />} />
            <Route path="/healthcare" element={<HealthcarePage />} />
            <Route path="/retail" element={<RetailPage />} />
            <Route path="/hotels" element={<HotelsPage />} />
            <Route path="/report-issue" element={<ReportIssuePage />} />
            <Route path="/login" element={<AuthPage />} />
            <Route path="/access-denied" element={<AccessDeniedPage />} />
            <Route
              path="/pricing"
              element={
                <ProtectedRoute>
                  <PricingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <RequireActivePlan>
                    <DashboardPage />
                  </RequireActivePlan>
                </ProtectedRoute>
              }
            />
            <Route
              path="/report-view"
              element={
                <ProtectedRoute>
                  <RequireActivePlan>
                    <ReportViewPage />
                  </RequireActivePlan>
                </ProtectedRoute>
              }
            />
            <Route
              path="/activity"
              element={
                <ProtectedRoute>
                  <ActivityLogsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics/native-visuals"
              element={
                <ProtectedRoute>
                  <NativeVisualsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/erp"
              element={
                <ProtectedRoute>
                  <RequirePaidProduct product="erp">
                    <TemplatesPage />
                  </RequirePaidProduct>
                </ProtectedRoute>
              }
            />
            <Route
              path="/templates"
              element={
                <ProtectedRoute>
                  <RequirePaidProduct product="erp">
                    <Navigate to="/erp" replace />
                  </RequirePaidProduct>
                </ProtectedRoute>
              }
            />
            <Route
              path="/visuals"
              element={
                <ProtectedRoute>
                  <SectorGuard>
                    <RequirePaidProduct product="erp">
                      <VisualsPage />
                    </RequirePaidProduct>
                  </SectorGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/visuals/create-table"
              element={
                <ProtectedRoute>
                  <SectorGuard>
                    <RequirePaidProduct product="erp">
                      <CreateTablePage />
                    </RequirePaidProduct>
                  </SectorGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/visuals/tables"
              element={
                <ProtectedRoute>
                  <SectorGuard>
                    <RequirePaidProduct product="erp">
                      <TablesPage />
                    </RequirePaidProduct>
                  </SectorGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/visuals/upload"
              element={
                <ProtectedRoute>
                  <SectorGuard>
                    <RequirePaidProduct product="erp">
                      <UploadPage />
                    </RequirePaidProduct>
                  </SectorGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/visuals/view-data"
              element={
                <ProtectedRoute>
                  <SectorGuard>
                    <RequirePaidProduct product="erp">
                      <ViewDataPage />
                    </RequirePaidProduct>
                  </SectorGuard>
                </ProtectedRoute>
              }
            />
            {/* AddDataPage route disabled - now using inline view in TablesPage */}
            {/* <Route
              path="/visuals/add-data/:schemaId"
              element={
                <ProtectedRoute>
                  <SectorGuard>
                    <AddDataPage />
                  </SectorGuard>
                </ProtectedRoute>
              }
            /> */}
            {/* ViewDataPage route disabled - now using inline view in TablesPage */}
            {/* <Route
              path="/visuals/view/:schemaId"
              element={
                <ProtectedRoute>
                  <SectorGuard>
                    <ViewDataPage />
                  </SectorGuard>
                </ProtectedRoute>
              }
            /> */}
            <Route
              path="/visuals/edit-table/:schemaId"
              element={
                <ProtectedRoute>
                  <SectorGuard>
                    <RequirePaidProduct product="erp">
                      <EditTablePage />
                    </RequirePaidProduct>
                  </SectorGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin-dashboard"
              element={
                <AdminRoute>
                  <AdminDashboard />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/fields"
              element={
                <AdminRoute>
                  <AdminFields />
                </AdminRoute>
              }
            />
            <Route
              path="/admin-dashboard/users/:user_id"
              element={
                <AdminRoute>
                  <AdminUserDetails />
                </AdminRoute>
              }
            />
            <Route
              path="/sectors"
              element={
                <AdminRoute>
                  <AdminSectors />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/entities"
              element={
                <AdminRoute>
                  <AdminSchools />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/entities/:id"
              element={
                <AdminRoute>
                  <AdminSchoolDetails />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/entities/:id/create-table"
              element={
                <AdminRoute>
                  <AdminCreateTablePage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/entities/:id/tables"
              element={
                <AdminRoute>
                  <AdminTablesPage />
                </AdminRoute>
              }
            />
            {/* AdminAddDataPage route disabled - now using inline view in AdminTablesPage */}
            <Route
              path="/admin/entities/:id/add-data/:schemaId"
              element={
                <AdminRoute>
                  <AdminAddDataPage />
                </AdminRoute>
              }
            />
            {/* AdminViewDataPage route disabled - now using inline view in AdminTablesPage */}
            <Route
              path="/admin/entities/:id/view/:schemaId"
              element={
                <AdminRoute>
                  <AdminViewDataPage />
                </AdminRoute>
              }
            />
            <Route
              path="/admin/entities/:id/edit-table/:schemaId"
              element={
                <AdminRoute>
                  <AdminEditTablePage />
                </AdminRoute>
              }
            />
            <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </main>
            {showPublicChrome && <Footer />}
          </div>
          <ToastContainer />
        </AuthProvider>
      </ToastProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
